import React from 'react'
import { Link } from '@tanstack/react-router'
import { ThemePicker } from '@/components/ThemePicker'
import { UserMenu } from '@/components/UserMenu'
import { BusswerksLogo } from '@/components/BusswerksLogo'
import { TransportChip } from '@/components/TransportChip'
import {
  WorkspaceSwitcher,
  type WorkspaceKey,
} from '@/components/WorkspaceSwitcher'
import { useAuth } from '@/hooks/useAuth'
import { usePlatform } from '@/hooks/usePlatform'

interface AppHeaderProps {
  /**
   * Big title on the left. Renders as a link to `/` by default so
   * clicking the brand always takes you home.
   */
  title?: string
  /**
   * Smaller subtitle under the title. Hidden on < sm breakpoints
   * to keep the header compact on mobile.
   */
  subtitle?: string
  /**
   * Right-hand action slot. Placed after the built-in ThemeToggle
   * and auth-aware Login/Dashboard link, so per-page actions like
   * "New Layout" or "Upgrade" flow naturally into the header.
   *
   * Wraps to a second line under ~640px via flex-wrap.
   */
  children?: React.ReactNode
  /**
   * When true, suppress the built-in auth-aware button (Login /
   * Dashboard). Used by the dashboard page which renders its own
   * tier badge + upgrade flow in `children` instead.
   */
  hideAuthAction?: boolean
  /**
   * Override the default brand-link target. When unset, the brand
   * routes to `/` on web (marketing landing) and `/home` on native
   * (Capacitor shells never show the landing page — see
   * `routes/index.tsx`). Pass an explicit value for per-page
   * overrides; pass `/` to opt a native page into routing-to-root
   * (almost certainly wrong).
   */
  brandHref?: string
  /**
   * Render the MIDI · Sequencer · Slicer switcher in the header's
   * right-side action bar, with the given workspace marked active.
   * Set only on the three workspace routes; omitted elsewhere (home,
   * login, editor, marketing pages). Clicking a pill is a SPA
   * navigation, not a full page reload.
   */
  workspaceSwitcher?: WorkspaceKey
  /**
   * Compact vertical-padding mode. Matches the WorkspaceHeader
   * used on editor / slicer / midi pages so non-editor pages
   * (home, dashboard) can share the slimmer header height when
   * the user is signed in and already knows the brand.
   */
  compact?: boolean
}

/**
 * Shared page header for home, dashboard, and any other non-editor
 * route. Responsive: on mobile the title stacks above the actions
 * via flex-wrap + gap. The editor has its own custom header because
 * it needs a dark bar with save status and layout breadcrumb.
 */
export const AppHeader: React.FC<AppHeaderProps> = ({
  title = 'Busswerks',
  subtitle,
  children,
  hideAuthAction = false,
  brandHref,
  workspaceSwitcher,
  compact = false,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth()
  const platform = usePlatform()
  // Native clients never show the landing page at `/`, so default
  // the brand to `/home`. Callers can still override with an
  // explicit `brandHref` prop if a page needs a different target.
  const resolvedBrandHref = brandHref ?? (platform.isNative ? '/home' : '/')

  return (
    <header className="bg-surface-raised border-b border-brd-subtle shadow-low pt-safe">
      <div
        className={`max-w-7xl mx-auto px-4 sm:px-6 ${
          compact ? 'py-2 sm:py-2.5' : 'py-2 sm:py-6'
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 sm:gap-x-4 sm:gap-y-3">
          {/* Brand + subtitle */}
          <Link
            to={resolvedBrandHref}
            className="min-w-0 flex-shrink"
            aria-label={title}
          >
            {title === 'Busswerks' ? (
              <BusswerksLogo
                size={compact ? 'sm' : 'lg'}
                className={
                  compact
                    ? 'text-content-primary text-xl sm:text-2xl'
                    : 'text-content-primary text-2xl sm:text-4xl'
                }
              />
            ) : (
              <h1
                className={`font-bold text-content-primary truncate leading-tight ${
                  compact
                    ? 'text-base sm:text-lg md:text-xl'
                    : 'text-lg sm:text-2xl md:text-3xl'
                }`}
              >
                {title}
              </h1>
            )}
            {subtitle && !compact && (
              <p className="hidden sm:block mt-0.5 sm:mt-1 text-xs sm:text-sm text-content-secondary truncate">
                {subtitle}
              </p>
            )}
          </Link>

          {/* Right-side action bar */}
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            {/* Transport chip surfaces playback controls on every page
                so users can stop / start the sequencer without
                having to navigate back to the editor. Hidden for
                unauthenticated visitors on marketing surfaces — the
                chip peeks at the sequencer store, which is not
                something anonymous-landing visitors need. */}
            {isAuthenticated && <TransportChip />}
            {workspaceSwitcher && (
              <WorkspaceSwitcher active={workspaceSwitcher} />
            )}
            {children}
            {!hideAuthAction && !isLoading && (
              <>
                {isAuthenticated && user ? (
                  <Link to="/home" className="btn-primary btn-sm">
                    Home
                  </Link>
                ) : (
                  <Link to="/login" className="btn-secondary btn-sm">
                    Login
                  </Link>
                )}
              </>
            )}
            {/* ThemePicker is shown standalone only for unauthenticated
                visitors. Authenticated users get it inside UserMenu. */}
            {!isAuthenticated && <ThemePicker />}
            <UserMenu />
          </div>
        </div>
      </div>
    </header>
  )
}
